/**
 * RunRequirementsDialog Component
 *
 * Shows what requirements are missing before a Run can be executed.
 * Always allows user to click Run, but shows this dialog if something is missing.
 */

import { Button, Modal } from '@affine/component';
import { useI18n } from '@affine/i18n';
import {
  CheckBoxCheckLinearIcon,
  CloseIcon,
  InformationIcon,
} from '@blocksuite/icons/rc';
import type { FC } from 'react';

// Status colours from the active theme table.
const colors = {
  warningBg: 'var(--streamlain-v2-layer-background-warning)',
  warningAccent: 'var(--streamlain-v2-status-warning)',
  errorBg: 'var(--streamlain-v2-layer-background-error)',
  errorAccent: 'var(--streamlain-v2-status-error)',
  successBg: 'var(--streamlain-v2-layer-background-success)',
  successAccent: 'var(--streamlain-v2-status-success)',
  textPrimary: 'var(--streamlain-v2-text-primary)',
  textSecondary: 'var(--streamlain-v2-text-secondary)',
  neutralBg: 'var(--streamlain-v2-layer-background-secondary)',
};

export interface RunRequirement {
  id: string;
  label: string;
  met: boolean;
  description?: string;
}

interface RunRequirementsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  requirements: RunRequirement[];
}

export const RunRequirementsDialog: FC<RunRequirementsDialogProps> = ({
  open,
  onOpenChange,
  requirements,
}) => {
  const t = useI18n();
  const unmetRequirements = requirements.filter(r => !r.met);
  const metRequirements = requirements.filter(r => r.met);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Requirements Not Met"
      width={480}
    >
      <div
        style={{
          padding: '16px 0',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        {/* Info Text */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            padding: '12px',
            backgroundColor: colors.warningBg,
            borderRadius: '8px',
          }}
        >
          <InformationIcon
            style={{
              width: '20px',
              height: '20px',
              color: colors.warningAccent,
              flexShrink: 0,
              marginTop: '2px',
            }}
          />
          <span
            style={{
              fontSize: '13px',
              lineHeight: 1.5,
              color: colors.textSecondary,
            }}
          >
            The following requirements must be met before running:
          </span>
        </div>

        {/* Requirements List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {/* Unmet requirements first */}
          {unmetRequirements.map(req => (
            <div
              key={req.id}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                padding: '12px',
                backgroundColor: colors.errorBg,
                borderRadius: '8px',
                border: `1px solid ${colors.errorAccent}`,
              }}
            >
              <CloseIcon
                style={{
                  width: '18px',
                  height: '18px',
                  color: colors.errorAccent,
                  flexShrink: 0,
                  marginTop: '1px',
                }}
              />
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: colors.errorAccent,
                    lineHeight: 1.4,
                  }}
                >
                  {req.label}
                </div>
                {req.description && (
                  <div
                    style={{
                      fontSize: '13px',
                      color: colors.textSecondary,
                      marginTop: '4px',
                      lineHeight: 1.5,
                    }}
                  >
                    {req.description}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Met requirements */}
          {metRequirements.map(req => (
            <div
              key={req.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px',
                backgroundColor: colors.neutralBg,
                borderRadius: '8px',
              }}
            >
              <CheckBoxCheckLinearIcon
                style={{
                  width: '18px',
                  height: '18px',
                  color: colors.successAccent,
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontSize: '14px',
                  color: colors.textSecondary,
                }}
              >
                {req.label}
              </span>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
            marginTop: '8px',
          }}
        >
          <Button onClick={() => onOpenChange(false)}>
            {t['com.streamlain.streamlain.common.close']()}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
